import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
} from "@mui/material";
import { useEmployeeService } from "../../services/EmployeeService";
import { useAppConfig } from "../../context/ConfigContext";

const FaceDataViewer = ({ empID = "", onApprove, onReject }) => {
  const { fetchFaceData } = useEmployeeService();
  const [faceData, setFaceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 🔁 Used to refetch
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    type: "",
    payload: null,
  });

  const { serverURL } = useAppConfig();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetchFaceData({ empId: empID || "All" }, false);
      if (res) setFaceData(res || []);
    } catch (err) {
      console.error("Failed to fetch face data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empID) fetchData();
  }, [empID, refreshTrigger]); // 👈 Re-fetch when trigger updates

  const handleConfirm = async () => {
    //  Close dialog immediately
    setConfirmationDialog({ open: false, type: "", payload: null });

    setActionLoading(true); // Then show loader

    try {
      if (confirmationDialog.type === "approve") {
        await onApprove?.(confirmationDialog.payload);
      } else if (confirmationDialog.type === "reject") {
        await onReject?.(confirmationDialog.payload);
      }

      setRefreshTrigger((prev) => prev + 1); // Refresh component
    } catch (err) {
      console.error("Action failed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const approved = faceData.find((f) => f.approved === "True");
  const rejected = faceData.every((f) => f.approved === "Rejected");
  const awaiting = faceData.filter((f) => f.approved === "false");

  let displayImages = [];
  let status = "";

  if (approved) {
    displayImages = faceData;
    status = "Approved";
  } else if (rejected) {
    displayImages = faceData;
    status = "Rejected";
  } else {
    displayImages = awaiting;
    status = "Awaiting Approval";
  }

  if (loading) return <CircularProgress />;
  if (!faceData.length)
    return <Alert severity="info">Face data: Yet to upload</Alert>;

  return (
    <Box>
      <Typography gutterBottom>Face Data - {status}</Typography>

      <Grid container spacing={2}>
        {displayImages.map((item) => {
          const imageUrl = `${item.imageString}`;
          return (
            <Grid item key={item.Id} xs={6} sm={3}>
              <Paper sx={{ p: 1, textAlign: "center", borderRadius: 2 }}>
                <Box
                  component="img"
                  src={imageUrl}
                  alt="Face"
                  sx={{
                    width: "100%",
                    height: 130,
                    objectFit: "cover",
                    borderRadius: 1,
                    mb: 1,
                  }}
                />
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={1} sx={{ mt: 2 }}>
        {status === "Awaiting Approval" && (
          <Grid item>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() =>
                setConfirmationDialog({
                  open: true,
                  type: "approve",
                  payload: displayImages[0],
                })
              }
              sx={{
                fontSize: "0.65rem",
                px: 2,
                py: 0.5,
                lineHeight: 1.3,
                minHeight: "28px",
              }}
            >
              Approve
            </Button>
          </Grid>
        )}

        {status !== "Rejected" && (
          <Grid item>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() =>
                setConfirmationDialog({
                  open: true,
                  type: "reject",
                  payload: faceData,
                })
              }
              sx={{
                fontSize: "0.65rem",
                px: 2,
                py: 0.5,
                lineHeight: 1.3,
                minHeight: "28px",
              }}
            >
              Reject
            </Button>
          </Grid>
        )}
      </Grid>

      {/* {status === "Awaiting Approval" && (
        <Grid container spacing={1} sx={{ mt: 2 }}>
          <Grid item>
            <Button
              variant="contained"
              color="success" // ✅ Green color
              size="small"
              onClick={() =>
                setConfirmationDialog({
                  open: true,
                  type: "approve",
                  payload: displayImages[0], // ✅ First awaiting image
                })
              }
              sx={{
                fontSize: "0.65rem",
                px: 2,
                py: 0.5,
                lineHeight: 1.3,
                minHeight: "28px",
              }}
            >
              Approve
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() =>
                setConfirmationDialog({
                  open: true,
                  type: "reject",
                  payload: faceData,
                })
              }
              sx={{
                fontSize: "0.65rem",
                px: 2,
                py: 0.5,
                lineHeight: 1.3,
                minHeight: "28px",
              }}
            >
              Reject
            </Button>
          </Grid>
        </Grid>
      )} */}

      <Dialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog({ open: false, type: "", payload: null })
        }
      >
        <DialogTitle>
          Confirm{" "}
          {confirmationDialog.type === "approve" ? "Approval" : "Rejection"}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to {confirmationDialog.type} this face data?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmationDialog({ open: false, type: "", payload: null })
            }
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={actionLoading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default FaceDataViewer;
